import LogoDarkHorizontal from '../../assets/images/logo-dark-horizontal.png';
import LogoHorizontal from '../../assets/images/logo-horizontal.png';
import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import SessionService from '../../services/SessionService';
import { useTheme } from '../../contexts/ThemeContext';
import { FinanceiroDataProvider, FinanceiroApiErrorBanner } from './FinanceiroDataContext';
import '../../styles/CombinedMenu/CombinedMenu.css';
import './FinanceiroModule.css';

const COLLAPSED_KEY = 'ligchat_fin_menu_collapsed';

export type FinNavItem = { to: string; label: string; iconClass: string };

export const FINANCEIRO_NAV: FinNavItem[] = [
  { to: '/financeiro/inicio', label: 'Dashboard', iconClass: 'icon-dashboard' },
  { to: '/financeiro/lancamentos', label: 'Lançamentos', iconClass: 'icon-flow' },
  { to: '/financeiro/cadastros', label: 'Cadastros', iconClass: 'icon-tag' },
];

function pathActive(pathname: string, to: string): boolean {
  if (to === '/financeiro/inicio') {
    return pathname === '/financeiro/inicio' || pathname === '/financeiro' || pathname === '/financeiro/';
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

const FinanceiroLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme, setTheme } = useTheme();
  const [phase, setPhase] = useState<'loading' | 'ok' | 'denied'>('loading');
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    const onResize = () => {
      const m = window.innerWidth < 768;
      setIsMobile(m);
      if (!m) setDrawerVisible(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleSidebar = useCallback(() => {
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setDrawerVisible((v) => !v);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = SessionService.getToken();
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!token || !apiUrl) {
        if (!cancelled) {
          setPhase('denied');
          navigate('/', { replace: true });
        }
        return;
      }
      try {
        const res = await fetch(`${apiUrl}/users/profile`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        });
        const json = await res.json().catch(() => null);
        const data = json?.data;
        const ok = Boolean(data?.canAccessFinanceiro ?? data?.CanAccessFinanceiro);
        if (cancelled) return;
        if (!ok) {
          setPhase('denied');
          navigate('/dashboard', { replace: true });
          return;
        }
        setPhase('ok');
      } catch {
        if (!cancelled) {
          setPhase('denied');
          navigate('/dashboard', { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (phase === 'loading') {
    return (
      <div className="menu-layout">
        <div className="fin-app__loading" style={{ minHeight: '100vh' }}>
          Carregando Ligchat Financeiro…
        </div>
      </div>
    );
  }

  if (phase === 'denied') return null;

  const sidebarCollapsed = collapsed && !isMobile;

  return (
    <div className="menu-layout">
      <header className="menu-header">
        <div className="menu-header-left">
          <button
            className="menu-toggle-button"
            type="button"
            onClick={isMobile ? toggleMobileMenu : toggleSidebar}
            aria-label={isMobile ? 'Abrir menu' : sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" aria-hidden>
              <path fill="currentColor" d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
            </svg>
          </button>
          <img
            src={resolvedTheme === 'dark' ? LogoDarkHorizontal : LogoHorizontal}
            alt="LigChat"
            className="menu-logo"
            onClick={() => navigate('/dashboard')}
            style={{ cursor: 'pointer', marginLeft: '10px' }}
          />
          <span className="fin-shell__product">Financeiro</span>
        </div>
        <div className="menu-header-right">
          <NavLink to="/dashboard" className="fin-shell__back-link" title="Voltar ao Ligchat">
            Voltar ao Ligchat
          </NavLink>
          <button
            className="menu-theme-toggle"
            type="button"
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            title={resolvedTheme === 'dark' ? 'Modo claro' : 'Modo escuro'}
            aria-label={resolvedTheme === 'dark' ? 'Modo claro' : 'Modo escuro'}
          >
            {resolvedTheme === 'dark' ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <circle cx="12" cy="12" r="5" />
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
        </div>
      </header>

      <div className="menu-content-layout">
        <aside
          className={`menu-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${drawerVisible && isMobile ? 'visible' : ''}`}
        >
          <div className="menu-sidebar-content">
            <ul className="menu-items" style={{ paddingBottom: 24 }}>
              {FINANCEIRO_NAV.map((item) => {
                const active = pathActive(location.pathname, item.to);
                return (
                  <li key={item.to} className={`menu-item${active ? ' active' : ''}`}>
                    <NavLink
                      to={item.to}
                      className="menu-item-content"
                      onClick={() => {
                        if (isMobile) setDrawerVisible(false);
                      }}
                    >
                      <span className={`menu-icon ${item.iconClass}`} aria-hidden />
                      <span className="menu-label">{item.label}</span>
                      {sidebarCollapsed ? <div className="menu-tooltip">{item.label}</div> : null}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        <main className="menu-content">
          <div className="page-container">
            <FinanceiroDataProvider>
              <>
                <FinanceiroApiErrorBanner />
                <Outlet />
              </>
            </FinanceiroDataProvider>
          </div>
        </main>
      </div>

      {isMobile && drawerVisible ? (
        <div className="menu-overlay" onClick={toggleMobileMenu} aria-hidden />
      ) : null}
    </div>
  );
};

export default FinanceiroLayout;
